import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useAction } from 'wasp/client/operations';
import { getLocationTypeById, updateLocationType } from 'wasp/client/operations';
import { useAuth } from 'wasp/client/auth';
import {
  Tag,
  Save,
  AlertCircle,
  HelpCircle,
  Layers,
  BookOpen
} from 'lucide-react';
import {
  IndividualEditPageLayout,
  SidebarSection,
  ActionButtons,
  ActionButton,
  ActionButtonSecondary
} from '../../components/layout';
import { toast } from '../../hooks/use-toast';
import LocTypeServiceTypesTab from './LocTypeServiceTypesTab';
import LocTypeTopicTypesTab from './LocTypeTopicTypesTab';

// Tooltip component (reusable)
const Tooltip = ({ children, content }: { children: React.ReactNode; content: string }) => {
  const [isVisible, setIsVisible] = useState(false);

  return (
    <div className="relative inline-block">
      <div
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
        className="cursor-help"
      >
        {children}
      </div>
      {isVisible && (
        <div className="absolute z-10 px-3 py-2 text-sm text-white bg-gray-900 rounded-lg shadow-lg bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-64">
          {content}
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
        </div>
      )}
    </div>
  );
};

interface LocationTypeFormData {
  key: string;
  label: string;
  description: string;
  isActive: boolean;
}

interface FormErrors {
  key?: string;
  label?: string;
  submit?: string;
}

export default function EditLocationTypePage() {
  const { locationTypeId } = useParams<{ locationTypeId: string }>();
  const { data: user } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [activeTab, setActiveTab] = useState<'details' | 'services' | 'topics'>('details');

  // Fetch data
  const { data: locationType, isLoading: isLocationTypeLoading, error: locationTypeError } = useQuery(
    getLocationTypeById,
    { id: locationTypeId! }
  );

  const updateLocationTypeFn = useAction(updateLocationType);

  const [formData, setFormData] = useState<LocationTypeFormData>({
    key: '',
    label: '',
    description: '',
    isActive: true
  });

  // Populate form when location type data is loaded
  useEffect(() => {
    if (locationType) {
      setFormData({
        key: locationType.key || '',
        label: locationType.label || '',
        description: locationType.description || '',
        isActive: locationType.isActive !== false
      });
    }
  }, [locationType?.id, locationType?.key, locationType?.label, locationType?.description, locationType?.isActive]);

  const handleChange = (field: keyof LocationTypeFormData, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.label.trim()) {
      newErrors.label = 'Label is required';
    }

    if (!formData.key.trim()) {
      newErrors.key = 'Key is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      await updateLocationTypeFn({
        id: locationTypeId!,
        key: formData.key,
        label: formData.label,
        description: formData.description || undefined,
        isActive: formData.isActive
      });

      toast({
        title: "Success",
        description: "Location type updated successfully"
      });

      navigate(`/admin/taxonomy/location-categories/${locationTypeId}`);
    } catch (error: any) {
      console.error('Error updating location type:', error);
      setErrors({ submit: error.message || 'Failed to update location type. Please try again.' });
      toast({
        title: "Error",
        description: error.message || 'Failed to update location type',
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    navigate(`/admin/taxonomy/location-categories/${locationTypeId}`);
  };

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Authentication Required</h2>
          <p className="text-gray-600">You must be logged in to edit location types.</p>
        </div>
      </div>
    );
  }

  if (isLocationTypeLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading location type...</p>
        </div>
      </div>
    );
  }

  if (locationTypeError || !locationType) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Tag className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Location Category Not Found</h3>
          <p className="text-gray-600 mb-4">The location type you're looking for doesn't exist or has been removed.</p>
          <button
            onClick={() => navigate('/admin/taxonomy/location-categories')}
            className="inline-flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Back to Location Categories
          </button>
        </div>
      </div>
    );
  }

  const sidebarContent = (
    <>
      <SidebarSection isFirst>
        <ActionButtons>
          <ActionButton
            type="submit"
            form="location-type-form"
            disabled={isLoading}
            icon={Save}
          >
            {isLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Updating...
              </>
            ) : (
              'Update'
            )}
          </ActionButton>

          <ActionButtonSecondary
            onClick={handleCancel}
            disabled={isLoading}
          >
            Cancel
          </ActionButtonSecondary>
        </ActionButtons>
      </SidebarSection>

      <SidebarSection title="Settings">
        <div className="space-y-4">
          <div className="space-y-3">
            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) => handleChange('isActive', e.target.checked)}
                className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500 rounded"
              />
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-700">Active Type</span>
                <Tooltip content="Mark this location type as active and visible in the system">
                  <HelpCircle className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                </Tooltip>
              </div>
            </label>
          </div>
        </div>
      </SidebarSection>

      <SidebarSection title="Help">
        <div className="space-y-2 text-sm text-gray-600">
          <p>• Label and key are required fields</p>
          <p>• Key must be unique across all location types</p>
          <p>• Locations may be using this type</p>
          <p>• Changes will affect all associated locations</p>
        </div>
      </SidebarSection>
    </>
  );

  return (
    <IndividualEditPageLayout sidebarContent={sidebarContent} layoutVariant="taxonomy" authUser={user}>
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-4">
          <Tag className="w-8 h-8 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Edit Location Category</h1>
            <p className="text-gray-600">Editing: {locationType.label}</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 mb-6">
        <button
          type="button"
          onClick={() => setActiveTab('details')}
          className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'details'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          <Tag className="w-4 h-4" />
          Details
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('services')}
          className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'services'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          <Layers className="w-4 h-4" />
          Services
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('topics')}
          className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'topics'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          <BookOpen className="w-4 h-4" />
          Topics
        </button>
      </div>

      {/* Form (Details tab) */}
      {activeTab === 'details' && <form id="location-type-form" onSubmit={handleSubmit}>
        {/* Primary Input Fields */}
        <div className="space-y-6 mb-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Label *</h3>
              <input
                type="text"
                value={formData.label}
                onChange={(e) => handleChange('label', e.target.value)}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors ${
                  errors.label ? 'border-destructive bg-destructive/10' : 'border-input'
                }`}
                placeholder="e.g., Apartment Building"
                required
              />
              {errors.label && (
                <p className="mt-1 text-sm text-destructive flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.label}
                </p>
              )}
            </div>

            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Key *</h3>
              <input
                type="text"
                value={formData.key}
                onChange={(e) => handleChange('key', e.target.value)}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors ${
                  errors.key ? 'border-destructive bg-destructive/10' : 'border-input'
                }`}
                placeholder="apartment-building"
                required
              />
              {errors.key && (
                <p className="mt-1 text-sm text-destructive flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.key}
                </p>
              )}
              <p className="text-sm text-gray-500 mt-2">Must be unique across all location types</p>
            </div>
          </div>

          {/* Description */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Description</h3>
            <textarea
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              rows={4}
              className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
              placeholder="Provide detailed description of the location type..."
            />
            <p className="text-sm text-gray-500 mt-2">{formData.description.length}/500 characters</p>
          </div>
        </div>

        {/* Current Information */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="text-sm font-medium text-gray-700 mb-3">Current Information</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Created</span>
                <span className="text-gray-900">
                  {new Date(locationType.createdAt).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Last Updated</span>
                <span className="text-gray-900">
                  {new Date(locationType.updatedAt).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Locations</span>
                <span className="text-gray-900">{locationType._count?.locations || 0}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Error message */}
        {errors.submit && (
          <div className="mt-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {errors.submit}
          </div>
        )}
      </form>}

      {/* Services Tab */}
      {activeTab === 'services' && (
        <LocTypeServiceTypesTab locationTypeId={locationTypeId!} />
      )}

      {/* Topics Tab */}
      {activeTab === 'topics' && (
        <LocTypeTopicTypesTab locationTypeId={locationTypeId!} />
      )}
    </IndividualEditPageLayout>
  );
}



